<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatIconForUI } from '@/lib/iconify'

const model = defineModel<string>({ default: '' })

const isOpen = ref(false)
const searchQuery = ref('')

// Comprehensive list of Lucide icons (most commonly used)
const ALL_ICONS = [
  // Charts & Analytics
  'chart-bar',
  'chart-line',
  'chart-pie',
  'chart-area',
  'chart-column',
  'chart-spline',
  'trending-up',
  'trending-down',
  'activity',
  'bar-chart',
  'bar-chart-2',
  'bar-chart-3',
  'pie-chart',
  'line-chart',
  'area-chart',
  'candlestick-chart',
  'gantt-chart',
  // Business & Finance
  'briefcase',
  'building',
  'building-2',
  'factory',
  'store',
  'warehouse',
  'wallet',
  'credit-card',
  'banknote',
  'coins',
  'piggy-bank',
  'receipt',
  'dollar-sign',
  'euro',
  'pound-sterling',
  'bitcoin',
  'percent',
  'calculator',
  'scale',
  'landmark',
  'badge-dollar-sign',
  // Users & People
  'user',
  'users',
  'user-plus',
  'user-minus',
  'user-check',
  'user-x',
  'user-circle',
  'user-cog',
  'users-round',
  'contact',
  'circle-user',
  // Communication
  'mail',
  'inbox',
  'send',
  'message-square',
  'message-circle',
  'messages-square',
  'phone',
  'phone-call',
  'video',
  'mic',
  'volume-2',
  'bell',
  'bell-ring',
  'at-sign',
  'hash',
  'megaphone',
  'radio',
  // Files & Documents
  'file',
  'file-text',
  'file-plus',
  'file-minus',
  'file-check',
  'file-x',
  'files',
  'folder',
  'folder-open',
  'folder-plus',
  'archive',
  'clipboard',
  'clipboard-list',
  'notebook',
  'book',
  'book-open',
  // Navigation & Location
  'home',
  'map',
  'map-pin',
  'compass',
  'navigation',
  'globe',
  'globe-2',
  'flag',
  'signpost',
  'milestone',
  'route',
  'locate',
  'crosshair',
  // Time & Calendar
  'calendar',
  'calendar-days',
  'calendar-check',
  'clock',
  'timer',
  'alarm-clock',
  'hourglass',
  'history',
  'watch',
  'stopwatch',
  // Settings & Tools
  'settings',
  'settings-2',
  'sliders',
  'sliders-horizontal',
  'wrench',
  'hammer',
  'cog',
  'gear',
  'tool',
  'nut',
  'screwdriver',
  'paintbrush',
  // Security & Privacy
  'lock',
  'unlock',
  'key',
  'shield',
  'shield-check',
  'shield-alert',
  'eye',
  'eye-off',
  'scan',
  'fingerprint',
  'badge-check',
  // Data & Database
  'database',
  'server',
  'hard-drive',
  'cpu',
  'memory-stick',
  'cloud',
  'cloud-upload',
  'cloud-download',
  'download',
  'upload',
  // UI & Layout
  'layout',
  'layout-grid',
  'layout-list',
  'grid',
  'list',
  'table',
  'columns',
  'sidebar',
  'panel-left',
  'panel-right',
  'layers',
  'stack',
  'menu',
  'more-horizontal',
  'more-vertical',
  'grip',
  'grip-vertical',
  // Arrows & Direction
  'arrow-up',
  'arrow-down',
  'arrow-left',
  'arrow-right',
  'chevron-up',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'move',
  'expand',
  'shrink',
  'maximize',
  'minimize',
  // Actions
  'plus',
  'minus',
  'x',
  'check',
  'check-circle',
  'x-circle',
  'edit',
  'edit-2',
  'edit-3',
  'pencil',
  'pen',
  'trash',
  'trash-2',
  'copy',
  'clipboard-copy',
  'scissors',
  'save',
  'undo',
  'redo',
  'refresh-cw',
  'rotate-cw',
  'rotate-ccw',
  'repeat',
  'shuffle',
  // Search & Filter
  'search',
  'filter',
  'sort-asc',
  'sort-desc',
  'zoom-in',
  'zoom-out',
  // Media
  'image',
  'camera',
  'video',
  'film',
  'music',
  'headphones',
  'play',
  'pause',
  'stop',
  'skip-forward',
  'skip-back',
  // Shopping & Commerce
  'shopping-cart',
  'shopping-bag',
  'package',
  'box',
  'gift',
  'tag',
  'tags',
  'barcode',
  'qr-code',
  'truck',
  'shipping',
  // Shapes & Objects
  'circle',
  'square',
  'triangle',
  'hexagon',
  'octagon',
  'star',
  'heart',
  'bookmark',
  'award',
  'trophy',
  'medal',
  'crown',
  'target',
  'crosshair',
  'bullseye',
  'aim',
  // Nature & Weather
  'sun',
  'moon',
  'cloud',
  'cloud-rain',
  'snowflake',
  'wind',
  'umbrella',
  'thermometer',
  'droplet',
  'flame',
  'leaf',
  'tree',
  'flower',
  // Devices
  'smartphone',
  'tablet',
  'laptop',
  'monitor',
  'tv',
  'watch',
  'printer',
  'keyboard',
  'mouse',
  'bluetooth',
  'wifi',
  'signal',
  // Social & Links
  'link',
  'link-2',
  'external-link',
  'share',
  'share-2',
  'forward',
  'thumbs-up',
  'thumbs-down',
  'heart',
  'star',
  'flag',
  // Alerts & Status
  'alert-circle',
  'alert-triangle',
  'info',
  'help-circle',
  'question',
  'check-circle',
  'x-circle',
  'ban',
  'slash',
  'loader',
  'loader-2',
  // Misc
  'zap',
  'bolt',
  'sparkles',
  'wand',
  'magic',
  'lightbulb',
  'rocket',
  'plane',
  'car',
  'bike',
  'anchor',
  'ship',
  'puzzle',
  'dice',
  'gamepad',
  'joystick',
  'code',
  'terminal',
  'binary',
  'brackets',
  'braces',
  'git-branch',
  'git-commit',
  'git-merge',
  'git-pull-request',
]

const displayIcons = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) {
    return ALL_ICONS
  }
  return ALL_ICONS.filter((icon) => icon.includes(query))
})

const hasResults = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return true
  return ALL_ICONS.some((icon) => icon.includes(query))
})

function selectIcon(icon: string) {
  model.value = `lucide-${icon}`
  isOpen.value = false
  searchQuery.value = ''
}

function clearIcon() {
  model.value = ''
  isOpen.value = false
}

function getDisplayName(icon: string): string {
  return icon.replace('lucide-', '')
}
</script>

<template>
  <UPopover v-model:open="isOpen" :ui="{ content: 'p-0' }">
    <UButton variant="outline" color="neutral" class="w-12 justify-center">
      <UIcon v-if="model" :name="formatIconForUI(model)" class="size-5" />
      <UIcon v-else name="i-lucide-smile-plus" class="size-5 text-gray-400" />
    </UButton>

    <template #content>
      <div class="w-80 p-3">
        <UInput
          v-model="searchQuery"
          placeholder="Search icons..."
          icon="i-lucide-search"
          size="sm"
          autofocus
          class="mb-3"
        />

        <div v-if="hasResults" class="grid grid-cols-8 gap-1 max-h-72 overflow-y-auto">
          <button
            v-for="icon in displayIcons"
            :key="icon"
            type="button"
            class="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            :class="{ 'bg-primary-100 dark:bg-primary-900': model === `lucide-${icon}` }"
            :title="icon"
            @click="selectIcon(icon)"
          >
            <UIcon :name="`i-lucide-${icon}`" class="size-4" />
          </button>
        </div>

        <div v-else class="text-center py-6 text-sm text-gray-500">
          No icons found for "{{ searchQuery }}"
        </div>

        <div
          v-if="model"
          class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between"
        >
          <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <UIcon :name="formatIconForUI(model)" class="size-4" />
            <span class="font-mono text-xs">{{ getDisplayName(model) }}</span>
          </div>
          <UButton size="xs" variant="ghost" color="error" @click="clearIcon"> Clear </UButton>
        </div>
      </div>
    </template>
  </UPopover>
</template>
